import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import { getImageUrl } from '../utils/image';
import {
  generateGameReportPDF,
  generateRoundReportPDF,
  generatePlayerHistoryPDF,
  downloadPDF,
  printPDF
} from '../components/prints/clubPrintTemplates';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler, EmptyState } from '../components/common/ErrorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Plus, Play, Users, UserCircle, Gamepad2, Trophy, X, Check, Clock,
  Edit, Trash2, Eye, Settings, BarChart3,
  Download, FileText, TrendingUp, TrendingDown, Filter, Calendar
} from 'lucide-react';

import useToast from '../hooks/useToast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

// Button Size System
const buttonSizes = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm',
  md: 'px-4 py-2 text-sm sm:px-5 sm:py-2.5 sm:text-base',
  lg: 'px-5 py-2.5 text-sm sm:px-6 sm:py-3 sm:text-base'
};

// ✅ Constants for DROP penalties
const DROP_POINTS = {
  STAKE: {
    FIRST_ROUND: -10,
    OTHER_ROUNDS: -20
  },
  SYNDICATE: {
    FIRST_DROP: 20,
    OTHER_DROPS: 40
  }
};

// ✅ Kitty Points calculation based on active player count
const calculateKittyPoints = (playerCount) => {
  if (playerCount <= 0) return 0;
  if (playerCount <= 4) return 1;
  if (playerCount <= 6) return 2;
  if (playerCount <= 8) return 3;
  if (playerCount === 9) return 4;
  return 5; // 10 or more
};

// Inline styles
const styles = `
  .fade-in {
    animation: fadeIn 0.5s ease-in;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .card-hover {
    transition: all 0.3s ease;
  }
  
  .card-hover:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  }
  
  @media print {
    .no-print { display: none !important; }
    body { background: white !important; }
    * { background: white !important; color: black !important; }
    .print-header { display: block !important; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
  }
  
  .print-header {
    display: none;
  }
  
  @media print {
    .print-header {
      display: block;
      text-align: center;
      margin-bottom: 20px;
      padding: 20px;
      border-bottom: 3px solid #10b981;
    }
  }
`;

// Kitty Dashboard
const KittyDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('week');
  const [chartData, setChartData] = useState([]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/api/kitty/dashboard', { params: { filter_type: filter } });
      setDashboard(response.data);
      setChartData(response.data.daily_kitty || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return <LoadingSpinner />;
  if (!dashboard) return <ErrorHandler />;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="kitty-dashboard">
      <style>{styles}</style>

      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Kitty Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Welcome to Kitty Entry System</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {['today', 'week', 'month'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`${buttonSizes.sm} rounded-lg font-semibold transition-all ${
                  filter === f ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'today' ? 'Today' : f === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <iv className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs sm:text-sm font-medium">Active Games</p>
              <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{dashboard?.active_games || 0}</h3>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs sm:text-sm font-medium">Total Games</p>
              <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{dashboard?.total_games || 0}</h3>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Gamepad2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs sm:text-sm font-medium">Today Kitty</p>
              <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{(dashboard?.today_kitty || 0).toLocaleString()}</h3>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-xl p-4 sm:p-6 shadow-lg card-hover">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-xs sm:text-sm font-medium">Monthly Kitty</p>
              <h3 className="text-3xl sm:text-4xl font-bold text-white mt-1 sm:mt-2">{(dashboard?.monthly_kitty || 0).toLocaleString()}</h3>
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>
      </iv>

      {/* Weekly Kitty Graph */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Daily Kitty Points (Last 7 Days)</h2>
        <div className="h-48 sm:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <RechartsTooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="kitty" fill="#10b981" name="Kitty Points ()" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            onClick={() => navigate('/kitty/create')}
            className="bg-gradient-to-r from-green-600 to-green-700 p-4 rounded-lg text-white hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
            data-testid="create-table-button"
          >
            <div className="flex items-center justify-center sm:justify-start space-x-3">
              <Plus className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
              <span className="font-semibold text-sm sm:text-base">CREATE TABLE</span>
            </div>
          </button>

          <button
            onClick={() => navigate('/kitty/live')}
            className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 rounded-lg text-white hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
            data-testid="live-games-button"
          >
            <div className="flex items-center justify-center sm:justify-start space-x-3">
              <Eye className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
              <span className="font-semibold text-sm sm:text-base">VIEW LIVE GAMES</span>
            </div>
          </button>

          <button
            onClick={() => navigate('/kitty/closed')}
            className="bg-gradient-to-r from-yellow-600 to-yellow-700 p-4 rounded-lg text-white hover:from-yellow-700 hover:to-yellow-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 duration-200"
            data-testid="closed-tables-button"
          >
            <div className="flex items-center justify-center sm:justify-start space-x-3">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
              <span className="font-semibold text-sm sm:text-base">CLOSED TABLES</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Table
const CreateTable = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [staff, setStaff] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    table_no: 1, table_type: 'STAKE', group_name: '40',
    pool_limit: 101, winning_amount: 5000,
    dealer: '', supervisor: '', cashier: '', players: []
  });
  const [saving, setSaving] = useState(false);
  const [manualPlayer, setManualPlayer] = useState('');
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [newStaffRole, setNewStaffRole] = useState('');
  const [newStaff, setNewStaff] = useState({ name: '', mobile: '' });

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/api/kitty/staff');
      setStaff(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = async (query) => {
    setSearch(query);
    if (query.length < 2) { setSearchResults([]); return; }
    try {
      const response = await api.get('/api/members/search/autocomplete', { params: { q: query } });
      setSearchResults(response.data);
    } catch (err) { console.error(err); }
  };

  const addMember = useCallback((member) => {
    setFormData(prev => {
      // ✅ Check using normalized ID
      if (prev.players.find(p => String(p.player_id) === String(member.id))) return prev;
      
      return {
        ...prev,
        players: [...prev.players, {
          player_id: String(member.id), // ✅ Normalize to string
          player_name: member.applicant_name,
          member_no: member.membership_no,
          is_member: true,
          profile_picture: member.profile_picture
        }]
      };
    });
    setSearch(''); 
    setSearchResults([]);
  }, []);

  const addManualPlayer = useCallback(() => {
    if (!manualPlayer.trim()) return;
    setFormData(prev => ({
      ...prev,
      players: [...prev.players, {
        player_id: `manual_${Date.now()}`, // ✅ Already string
        player_name: manualPlayer.trim(),
        member_no: null,
        is_member: false,
        profile_picture: null
      }]
    }));
    setManualPlayer('');
  }, [manualPlayer]);

  const removePlayer = useCallback((index) => {
    setFormData(prev => ({ 
      ...prev, 
      players: prev.players.filter((_, i) => i !== index) 
    }));
  }, []);

  const displayKittyPoints = useMemo(() => {
    return calculateKittyPoints(formData.players.length);
  }, [formData.players.length]);

  const handleAddStaff = async () => {
    try {
      await api.post('/api/kitty/staff', { name: newStaff.name, mobile: newStaff.mobile, staff_type: newStaffRole });
      showSuccess('Staff added successfully');
      setShowStaffModal(false);
      setNewStaff({ name: '', mobile: '' });
      fetchStaff();
    } catch (err) {
      showError('Failed to add staff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.players.length < 2) { showError('Add at least 2 players'); return; }
    try {
      setSaving(true);
      await api.post('/api/kitty/tables', formData);
      showSuccess('Table created successfully');
      navigate('/kitty/live');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to create table');
    } finally {
      setSaving(false);
    }
  };

  const dealers = useMemo(() => staff.filter(s => s.staff_type === 'Dealer'), [staff]);
  const supervisors = useMemo(() => staff.filter(s => s.staff_type === 'Supervisor'), [staff]);
  const cashiers = useMemo(() => staff.filter(s => s.staff_type === 'Cashier'), [staff]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 max-w-7xl mx-auto fade-in p-2 sm:p-4" data-testid="create-table-form">
      <style>{styles}</style>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Create New Kitty Table</h1>
      </div>

      {/* Table & Staff Details */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Table & Staff Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Game No</label>
            <input
              type="text"
              value="Auto-generated"
              disabled
              className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-500 cursor-not-allowed text-sm"
              data-testid="game-no-display"
            />
            <p className="text-xs text-gray-500 mt-1">Assigned by system</p>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Table No *</label>
            <input
              type="number"
              min="1"
              value={formData.table_no}
              onChange={(e) => setFormData(prev => ({...prev, table_no: parseInt(e.target.value)}))}
              required
              className="w-full bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              data-testid="table-no-input"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Table Type *</label>
            <select
              value={formData.table_type}
              onChange={(e) => setFormData(prev => ({...prev, table_type: e.target.value}))}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              data-testid="table-type-select"
            >
              <option value="STAKE">STAKE</option>
              <option value="SYNDICATE">SYNDICATE</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Group Name *</label>
            <input
              value={formData.group_name}
              onChange={(e) => setFormData(prev => ({...prev, group_name: e.target.value}))}
              required
              placeholder="e.g., 40, 80"
              className="w-full bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              data-testid="group-name-input"
            />
          </div>

          {formData.table_type === 'SYNDICATE' && (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Pool Limit *</label>
                <input
                  type="number"
                  value={formData.pool_limit}
                  onChange={(e) => setFormData(prev => ({...prev, pool_limit: parseInt(e.target.value)}))}
                  required
                  placeholder="e.g., 101"
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  data-testid="pool-limit-input"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Winning Amount () *</label>
                <input
                  type="number"
                  value={formData.winning_amount}
                  onChange={(e) => setFormData(prev => ({...prev, winning_amount: parseFloat(e.target.value)}))}
                  required
                  placeholder="e.g., 5000"
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  data-testid="winning-amount-input"
                />
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
          {['dealer', 'supervisor', 'cashier'].map(role => {
            const staffList = role === 'dealer' ? dealers : role === 'supervisor' ? supervisors : cashiers;
            return (
              <div key={role}>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  {role.charAt(0).toUpperCase() + role.slice(1)} {role === 'dealer' && '*'}
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData[role]}
                    onChange={(e) => setFormData(prev => ({...prev, [role]: e.target.value}))}
                    required={role === 'dealer'}
                    className="flex-1 bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    data-testid={`${role}-select`}
                  >
                    <option value="">Select {role.charAt(0).toUpperCase() + role.slice(1)}</option>
                    {staffList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setNewStaffRole(role.charAt(0).toUpperCase() + role.slice(1)); setShowStaffModal(true); }}
                    className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold flex-shrink-0"
                    data-testid={`add-${role}-button`}
                    title="Add New Staff"
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Member Management */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">
            Member Management
            <span className="ml-2 text-sm font-medium text-gray-600">
              ({formData.players.length} selected)
            </span>
          </h2>

          {formData.table_type === 'STAKE' && formData.players.length > 0 && (
            <div className="text-yellow-600 font-semibold text-xs sm:text-sm px-3 py-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
              Kitty Points: {displayKittyPoints} KP / round
            </div>
          )}
        </div>

        {/* Search Members */}
        <div className="relative mb-3 sm:mb-4">
          <input
            placeholder="Search by Member No, Name, or Mobile"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            data-testid="search-member-input"
          />

          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => addMember(m)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-900 border-b border-gray-200 last:border-0"
                >
                  {m.profile_picture ? (
                    <img
                      src={getImageUrl(m.profile_picture)}
                      alt={m.applicant_name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <UserCircle className="w-10 h-10 text-gray-400 flex-shrink-0" />
                  )}

                  <div className="min-w-0">
                    <p className="font-semibold truncate text-sm">{m.applicant_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      Member No: {m.membership_no} • {m.mobile}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Manual Player Entry */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:mb-5">
          <input
            placeholder="Add manual player (Guest / Temporary)"
            value={manualPlayer}
            onChange={(e) => setManualPlayer(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addManualPlayer())}
            className="flex-1 bg-white border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            data-testid="manual-player-input"
          />

          <button
            type="button"
            onClick={addManualPlayer}
            className={`${buttonSizes.md} bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center justify-center gap-2 w-full sm:w-auto`}
            data-testid="add-manual-player-button"
          >
            <Plus className="w-4 h-4" />
            Add Player
          </button>
        </div>

        {/* Selected Members */}
        {formData.players.length === 0 ? (
          <div className="text-center py-8 sm:py-10 text-gray-500">
            <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 opacity-50" />
            <p className="font-medium text-sm sm:text-base">No members added yet</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">
              Search or add players to begin
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
            {formData.players.map((player, i) => (
              <div
                key={i}
                className="relative p-2 sm:p-3 bg-gray-50 rounded-lg text-center border border-gray-200 hover:border-green-300 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => removePlayer(i)}
                  className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-lg"
                  title="Remove Player"
                  data-testid={`remove-player-${i}`}
                >
                  <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                </button>

                {player.profile_picture ? (
                  <img
                    src={getImageUrl(player.profile_picture)}
                    alt={player.player_name}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover mx-auto mb-1.5 sm:mb-2 shadow-md"
                  />
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-sm sm:text-base mx-auto mb-1.5 sm:mb-2 shadow-lg">
                    {player.player_name.charAt(0).toUpperCase()}
                  </div>
                )}

                <p className="text-xs font-semibold text-gray-900 truncate">
                  {player.player_name}
                </p>

                {player.member_no && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {player.member_no}
                  </p>
                )}

                <span
                  className={`inline-block mt-1 px-1.5 sm:px-2 py-0.5 rounded text-xs font-semibold ${
                    player.is_member
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  {player.is_member ? 'Member' : 'Guest'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4">
        <button
          type="button"
          onClick={() => navigate('/kitty')}
          className={`${buttonSizes.md} bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold order-2 sm:order-1`}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || formData.players.length < 2}
          className={`${buttonSizes.md} bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed shadow-lg order-1 sm:order-2`}
          data-testid="start-table-button"
        >
          {saving ? 'Creating...' : 'START TABLE'}
        </button>
      </div>

      {/* Add Staff Modal */}
      <Dialog open={showStaffModal} onOpenChange={setShowStaffModal}>
        <DialogContent className="bg-white border-gray-300 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add New {newStaffRole}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                value={newStaff.name}
                onChange={(e) => setNewStaff(prev => ({...prev, name: e.target.value}))}
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="new-staff-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label>
              <input
                value={newStaff.mobile}
                onChange={(e) => setNewStaff(prev => ({...prev, mobile: e.target.value}))}
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="new-staff-mobile-input"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleAddStaff}
                disabled={!newStaff.name || !newStaff.mobile}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                data-testid="confirm-add-staff-button"
              >
                Add Staff
              </button>
              <button
                type="button"
                onClick={() => setShowStaffModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
};

// Live Tables
const LiveTables = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchTables();
    const interval = setInterval(fetchTables, 30000);
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, []);

  const fetchTables = async () => {
    try {
      const response = await api.get('/api/kitty/tables', {
        params: { status: 'LIVE' }
      });
      setTables(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateElapsedTime = useCallback((startTime) => {
    const start = new Date(startTime);
    const diff = currentTime - start;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}h ${minutes
      .toString()
      .padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
  }, [currentTime]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="live-tables">
      <style>{styles}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Live Tables</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {tables.length} active table(s)
          </p>
        </div>

        <button
          onClick={() => navigate('/kitty/create')}
          className={`${buttonSizes.md} bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg hover:from-green-700 hover:to-green-800 shadow-lg w-full sm:w-auto`}
          data-testid="new-table-button"
        >
          + New Table
        </button>
      </div>

      {/* Empty State */}
      {tables.length === 0 ? (
        <div className="bg-white rounded-xl p-8 sm:p-12 text-center border border-gray-200">
          <Gamepad2 className="w-16 h-16 sm:w-24 sm:h-24 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            No Active Tables
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4">
            Start a new table to begin playing
          </p>
          <button
            onClick={() => navigate('/kitty/create')}
            className={`${buttonSizes.md} bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold`}
          >
            Create First Table
          </button>
        </div>
      ) : (
        /* Tables Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {tables.map((table) => (
            <div
              key={table.id}
              className={`rounded-xl p-3 sm:p-6 shadow-lg border-2 transition-all card-hover
  ${table.table_type === 'STAKE'
    ? 'bg-indigo-50 border-indigo-300'
    : 'bg-amber-50 border-amber-300'
  }`}

              data-testid={`table-card-${table.id}`}
            >
              {/* Card Header */}
<div className="flex items-center justify-between mb-2">
  <div className="flex-1 min-w-0">
    {/* Game No */}
    <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
      {table.game_no}
    </h3>

    {/* Table Type + Table No */}
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      <span
        className={`px-2 sm:px-3 py-1 rounded-lg text-xs font-semibold border ${
          table.table_type === 'STAKE'
            ? 'bg-blue-100 text-blue-700 border-blue-300'
            : 'bg-purple-100 text-purple-700 border-purple-300'
        }`}
      >
        {table.table_type}
      </span>

      {/* 🔴 Table No badge */}
      <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-red-600 text-white">
        TABLE-{table.table_no}
      </span>
    </div>
  </div>

  {/* LIVE badge */}
  <div className="px-2 sm:px-3 py-1 bg-green-100 border border-green-300 rounded-lg flex-shrink-0 ml-2">
    <span className="text-green-700 font-semibold flex items-center gap-1 text-xs sm:text-sm">
      <Clock className="w-3 h-3" /> LIVE
    </span>
  </div>
</div>


              {/* Table Details */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3 text-xs sm:text-sm">

                <div className="flex justify-between text-gray-600">
                  <span>Group:</span>
                  <span className="text-gray-900 font-medium">
                    {table.group_name}
                  </span>
                </div>

                <div className="flex justify-between text-gray-600">
                  <span>Current Round:</span>
                  <span className="text-yellow-600 font-bold">
                    {table.current_round}
                  </span>
                </div>

                <div className="flex justify-between text-gray-600">
                  <span>Members:</span>
                  <span className="text-gray-900 font-medium">
                    {table.players?.length || 0}
                  </span>
                </div>

                {table.table_type === 'STAKE' && (
                  <div className="flex justify-between text-gray-600">
                    <span>Kitty Points:</span>
                    <span className="text-yellow-600 font-bold">
                      {table.kitty_points} KP
                    </span>
                  </div>
                )}

                {table.table_type === 'SYNDICATE' && (
                  <>
                    <div className="flex justify-between text-gray-600">
                      <span>Pool Limit:</span>
                      <span className="text-gray-900 font-medium">
                        {table.pool_limit}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Win Amount:</span>
                      <span className="text-green-600 font-semibold">
                        {table.winning_amount?.toLocaleString()}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Staff */}
              <div className="mb-3 p-2 bg-gray-50 rounded-md text-[11px] grid grid-cols-2 gap-y-1">
                <div className="flex justify-between text-gray-600">
                  <span>Dealer:</span>
                  <span className="text-gray-900 font-medium">
                    {table.dealer}
                  </span>
                </div>
                {table.supervisor && (
                  <div className="flex justify-between text-gray-600">
                    <span>Supervisor:</span>
                    <span className="text-gray-900 font-medium">
                      {table.supervisor}
                    </span>
                  </div>
                )}
                {table.cashier && (
                  <div className="flex justify-between text-gray-600">
                    <span>Cashier:</span>
                    <span className="text-gray-900 font-medium">
                      {table.cashier}
                    </span>
                  </div>
                )}
              </div>

              {/* Time */}
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col gap-1">
                {/* Started */}
                <div className="flex justify-between items-center text-xs text-blue-700">
                  <span className="font-medium">Started</span>
                  <span className="font-semibold">
                    {new Date(table.started_at).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                {/* Elapsed */}
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Elapsed
                  </span>
                  <span className="text-sm sm:text-lg font-mono font-bold text-blue-700">
                    {calculateElapsedTime(table.started_at)}
                  </span>
                </div>
              </div>


              {/* Players */}
              <div className="mb-4">
                <p className="text-xs text-gray-600 mb-2">Players:</p>
                <div className="flex flex-wrap gap-2">
                  {table.players?.slice(0, 8).map((p, i) =>
                    p.profile_picture ? (
                      <img
                        key={i}
                        src={getImageUrl(p.profile_picture)}
                        alt={p.player_name}
                        title={p.player_name}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shadow-lg border-2 border-gray-300"
                      />
                    ) : (
                      <div
                        key={i}
                        title={p.player_name}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600
                                   flex items-center justify-center text-white font-semibold text-xs sm:text-sm
                                   shadow-lg border-2 border-gray-300"
                      >
                        {p.player_name.charAt(0).toUpperCase()}
                      </div>
                    )
                  )}

                  {table.players?.length > 8 && (
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-300 flex items-center justify-center
                                    text-gray-700 font-semibold text-xs border-2 border-gray-400">
                      +{table.players.length - 8}
                    </div>
                  )}
                </div>
              </div>

              {/* Action */}
              <button
                onClick={() => navigate(`/kitty/game/${table.id}`)}
                className={`${buttonSizes.md} w-full bg-gradient-to-r from-emerald-600 to-emerald-700
hover:from-emerald-700 hover:to-emerald-800 text-white font-bold rounded-lg
                           shadow-lg`}
                data-testid={`open-table-${table.id}`}
              >
                OPEN TABLE
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ FULLY UPDATED Live Game Component with Correct Logic
const LiveGame = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({});
  const [winner, setWinner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showRemovePlayer, setShowRemovePlayer] = useState(false);
  const [newPlayerSearch, setNewPlayerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [manualPlayer, setManualPlayer] = useState('');

  useEffect(() => { fetchTable(); }, [id]);

  // ✅ Fetch table data and initialize scores
  const fetchTable = async () => {
    try {
      const response = await api.get(`/api/kitty/tables/${id}`);
      setTable(response.data);

      // ✅ Initialize scores for active players only
      const initialScores = {};
      response.data.players
        ?.filter(p => p.status === 'Active')
        .forEach(p => {
          const pid = String(p.player_id);
          initialScores[pid] = {
            points: 0,
            action: 'SCORE'
          };
        });

      setScores(initialScores);
      setWinner(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Clear round data
  const handleClearRound = useCallback(() => {
    if (!window.confirm('Clear current round data? This will reset all scores and winner.')) {
      return;
    }

    const resetScores = {};
    table.players
      ?.filter(p => p.status === 'Active')
      .forEach(p => {
        const pid = String(p.player_id);
        resetScores[pid] = {
          points: 0,
          action: 'SCORE'
        };
      });

    setWinner(null);
    setScores(resetScores);
    showSuccess('Current round data cleared');
  }, [table, showSuccess]);

  // ✅ Handle SHOW button click
  const handleShow = useCallback((playerId, playerName) => {
    const pid = String(playerId);

    if (scores[pid]?.action === 'DROP') {
      showError('Cannot SHOW after DROP');
      return;
    }

    if (winner) {
      showError('Winner already selected');
      return;
    }

    setWinner({
      player_id: pid,
      player_name: playerName
    });

    setScores(prev => ({
      ...prev,
      [pid]: {
        points: 0,
        action: 'SHOW'
      }
    }));
  }, [scores, winner, showError]);

  // ✅ Handle DROP button click with correct logic
  const handleDrop = useCallback((playerId) => {
    const pid = String(playerId);
    const isFirstRound = table?.current_round === 1;

    let dropPoints;
    if (table?.table_type === 'STAKE') {
      // STAKE: Round 1 = -10, Round 2+ = -20
      dropPoints = isFirstRound ? DROP_POINTS.STAKE.FIRST_ROUND : DROP_POINTS.STAKE.OTHER_ROUNDS;
    } else {
      // SYNDICATE: 1st drop = +20, 2nd+ drop = +40
      // Note: Backend should track drop count per player
      dropPoints = isFirstRound ? DROP_POINTS.SYNDICATE.FIRST_DROP : DROP_POINTS.SYNDICATE.OTHER_DROPS;
    }

    setScores(prev => ({
      ...prev,
      [pid]: {
        points: dropPoints,
        action: 'DROP'
      }
    }));
  }, [table]);

  // ✅ Calculate winner points (STAKE only)
  const calculateWinnerPoints = useMemo(() => {
    if (!winner || !table) return 0;

    // ✅ SYNDICATE: round winner always gets 0
    if (table.table_type === 'SYNDICATE') {
      return 0;
    }

    // ✅ STAKE calculation
    const winnerId = String(winner.player_id);

    // Sum of opponent scores (excluding winner)
    const otherPlayers = Object.entries(scores).filter(
      ([pid, data]) =>
        pid !== winnerId &&
        data.action === 'SCORE'
    );

    // Get dropped player penalties
    const droppedPlayers = Object.values(scores).filter(
      d => d.action === 'DROP'
    );

    const otherSum = otherPlayers.reduce(
      (sum, [_, data]) => sum + (data.points || 0),
      0
    );

    const dropGain = droppedPlayers.reduce(
      (sum, d) => sum + Math.abs(d.points || 0),
      0
    );

    const kittyFee = table.kitty_points || 0;

    // Winner = Opponent losses + Drop gains - Kitty Points
    return otherSum + dropGain - kittyFee;
  }, [winner, scores, table]);

  // ✅ Check if syndicate game should end (only 1 active left after round)
  const shouldEndSyndicateGame = useCallback((roundScores) => {
    if (table?.table_type !== 'SYNDICATE') return false;

    const activePlayers = table.players.filter(p => p.status === 'Active');
    if (activePlayers.length !== 2) return false;

    // Check if any player will reach pool limit after this round
    return activePlayers.some(p => {
      const pid = String(p.player_id);
      const roundScore = roundScores.find(
        r => String(r.player_id) === pid
      );
      const projectedTotal =
        (p.total_points || 0) + (roundScore?.points || 0);

      return projectedTotal >= table.pool_limit;
    });
  }, [table]);

  // ✅ Save round with validation
  const handleSaveRound = async () => {
    if (!winner) {
      showError('Please select a winner by clicking SHOW');
      return;
    }

    const winnerId = String(winner.player_id);

    // ✅ Validate non-winner scores
    const invalidPlayers = Object.entries(scores).filter(([pid, data]) =>
      pid !== winnerId &&
      data.action === 'SCORE' &&
      (data.points === null || data.points === undefined || Number.isNaN(data.points))
    );

    if (invalidPlayers.length > 0) {
      showError('Please enter points for all active players');
      return;
    }

    try {
      setSaving(true);
      const winnerPoints = calculateWinnerPoints;

      // Build player lookup map
      const playerMap = {};
      table.players.forEach(p => {
        playerMap[String(p.player_id)] = p;
      });

      const roundScores = Object.entries(scores).map(([playerId, data]) => {
        const player = playerMap[playerId];
        const isWinner = playerId === winnerId;

        return {
          player_id: playerId,
          player_name: player?.player_name,
          points: isWinner ? winnerPoints : data.points,
          action: data.action,
          is_winner: isWinner
        };
      });

      await api.post(`/api/kitty/tables/${id}/rounds`, { scores: roundScores });

      // ✅ Check if syndicate game should end
      if (shouldEndSyndicateGame(roundScores)) {
        await api.put(`/api/kitty/tables/${id}/close`);
        showSuccess('Final round completed. Syndicate game ended.');
        navigate('/kitty/closed');
        return;
      }

      showSuccess(`Round ${table.current_round} saved successfully`);
      fetchTable();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save round');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPlayerSearch = async (query) => {
    setNewPlayerSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await api.get('/api/members/search/autocomplete', { params: { q: query } });
      setSearchResults(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddMember = async (member) => {
    try {
      await api.post(`/api/kitty/tables/${id}/players`, {
        player_id: String(member.id),
        player_name: member.applicant_name,
        is_member: true,
        profile_picture: member.profile_picture
      });
      showSuccess('Player added to game');
      setShowAddPlayer(false);
      setNewPlayerSearch('');
      setSearchResults([]);
      fetchTable();
    } catch (err) {
      showError('Failed to add player');
    }
  };

  const handleAddManualPlayer = async () => {
    if (!manualPlayer.trim()) return;
    try {
      await api.post(`/api/kitty/tables/${id}/players`, {
        player_id: `manual_${Date.now()}`,
        player_name: manualPlayer.trim(),
        is_member: false,
        profile_picture: null
      });
      showSuccess('Manual player added');
      setShowAddPlayer(false);
      setManualPlayer('');
      fetchTable();
    } catch (err) {
      showError('Failed to add player');
    }
  };

  const handleRemovePlayer = async (playerId) => {
    if (!window.confirm('Remove this player from the game?')) return;
    try {
      await api.delete(`/api/kitty/tables/${id}/players/${playerId}`);
      showSuccess('Player removed');
      setShowRemovePlayer(false);
      fetchTable();
    } catch (err) {
      showError('Failed to remove player');
    }
  };

  const handleCloseTable = async () => {
    if (!window.confirm('Close this table? This will end the game.')) return;
    try {
      await api.put(`/api/kitty/tables/${id}/close`);
      showSuccess('Table closed successfully');
      navigate('/kitty/closed');
    } catch (err) {
      showError('Failed to close table');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!table) return <EmptyState title="Table not found" />;

  const activePlayers = table.players?.filter(p => p.status === 'Active') || [];

  return (
    <div className="space-y-3 sm:space-y-4 fade-in p-2 sm:p-4" data-testid="live-game">
      <style>{styles}</style>

      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-3 sm:p-4 shadow-lg border border-gray-700">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5 sm:mb-2">
              <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-green-600/20 border border-green-500/50 rounded-lg">
                <span className="text-green-400 font-semibold flex items-center gap-1 text-xs">
                  <Clock className="w-3 h-3" /> LIVE
                </span>
              </span>
              <span className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-xs font-semibold ${
                table.table_type === 'STAKE' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50' : 'bg-purple-600/20 text-purple-400 border border-purple-500/50'
              }`}>
                {table.table_type}
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-white truncate">
              Game {table.game_no} • Round {table.current_round}
            </h2>
            <p className="text-xs sm:text-sm text-gray-400 mt-0.5 truncate">
              Table {table.table_no} • Group {table.group_name}
            </p>
          </div>
          <div className="text-center sm:text-right flex-shrink-0">
            {table.table_type === 'STAKE' && (
              <>
                <p className="text-xs text-gray-400">Kitty Points</p>
                <p className="text-2xl sm:text-3xl font-bold text-yellow-400">{table.kitty_points} KP</p>
                <p className="text-xs text-gray-400 mt-0.5">{activePlayers.length} players</p>
              </>
            )}
            {table.table_type === 'SYNDICATE' && (
              <>
                <p className="text-xs text-gray-400">Pool Limit</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-400">{table.pool_limit}</p>
                <p className="text-green-400 font-semibold mt-0.5 text-xs sm:text-sm">Win: {table.winning_amount?.toLocaleString()}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        <div className="text-gray-900">
          <p className="text-xs sm:text-sm">Current Players: <span className="font-bold">{activePlayers.length}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowAddPlayer(true)}
            className={`${buttonSizes.xs} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-1.5`}
            data-testid="add-player-button"
          >
            <Plus className="w-3 h-3" /> ADD
          </button>
          <button
            onClick={() => setShowRemovePlayer(true)}
            className={`${buttonSizes.xs} bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center gap-1.5`}
            data-testid="remove-player-button"
          >
            <X className="w-3 h-3" /> REMOVE
          </button>
        </div>
      </div>

      {/* Player Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
        {activePlayers.map((player) => {
          const pid = String(player.player_id);
          const isWinner = winner?.player_id === pid;
          const playerScore = scores[pid];
          const isDrop = playerScore?.action === 'DROP';

          return (
            <div
              key={pid}
              className={`bg-white rounded-lg shadow-lg border-2 transition-all flex flex-col ${
                isWinner
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-500/50'
                  : isDrop
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200'
              }`}
              data-testid={`player-card-${pid}`}
            >
              <div className="p-2 sm:p-3 flex-1 flex flex-col">
                <div className="text-center mb-2">
                  {player.profile_picture ? (
                    <img
                      src={getImageUrl(player.profile_picture)}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover mx-auto mb-1.5"
                      alt={player.player_name}
                    />
                  ) : (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-sm sm:text-base mx-auto mb-1.5 shadow-lg">
                      {player.player_name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <p className="font-bold text-gray-900 truncate text-xs">
                    {player.player_name}
                  </p>

                  {table.table_type === 'SYNDICATE' && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Total:{' '}
                      <span className="font-bold text-orange-600">
                        {player.total_points || 0}
                      </span>
                    </p>
                  )}

                  <p
                    className={`text-xs font-bold mt-0.5 ${
                      isWinner
                        ? 'text-green-600'
                        : isDrop
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {isWinner ? '🏆 WIN' : isDrop ? '❌ DROP' : '🎮 Play'}
                  </p>
                </div>

                {/* Score Area */}
                <div className="mb-2 flex-1 flex items-center justify-center">
                  {isWinner ? (
                    <div className="w-full">
                      <p className="text-xs text-gray-600 mb-0.5 text-center">
                        Winner
                      </p>
                      <div className="text-base sm:text-lg font-bold text-green-600 text-center bg-green-100 rounded-lg py-1.5">
                        {calculateWinnerPoints}
                      </div>
                    </div>
                  ) : isDrop ? (
                    <div className="w-full">
                      <p className="text-xs text-gray-600 mb-0.5 text-center">
                        Drop
                      </p>
                      <div className="text-base sm:text-lg font-bold text-red-600 text-center bg-red-100 rounded-lg py-1.5">
                        {playerScore.points}
                      </div>
                    </div>
                  ) : winner ? (
                    <div className="w-full">
                      <label className="block text-xs font-medium text-gray-700 mb-0.5 text-center">
                        Points
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={playerScore?.points || ''}
                        onChange={(e) =>
                          setScores(prev => ({
                            ...prev,
                            [pid]: {
                              points: Number(e.target.value) || 0,
                              action: 'SCORE'
                            }
                          }))
                        }
                        className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-base font-bold text-center focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="0"
                        data-testid={`score-input-${pid}`}
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 text-center">
                      Waiting
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {!winner && !isDrop && (
                <div className="px-2 pb-2 sm:px-3 sm:pb-3">
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => handleShow(pid, player.player_name)}
                      className="w-full py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-xs flex items-center justify-center gap-1"
                      data-testid={`show-${pid}`}
                    >
                      <Check className="w-3 h-3" /> SHOW
                    </button>
                    <button
                      onClick={() => handleDrop(pid)}
                      className="w-full py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-xs flex items-center justify-center gap-1"
                      data-testid={`drop-${pid}`}
                    >
                      <X className="w-3 h-3" /> DROP
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3">
        <button
          onClick={() => navigate('/kitty/live')}
          className={`${buttonSizes.sm} bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 shadow-lg`}
        >
          ← Back
        </button>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleClearRound}
            className={`${buttonSizes.xs} bg-yellow-500 text-white font-bold rounded-lg hover:bg-yellow-600 shadow-lg`}
            data-testid="clear-round-button"
          >
            CLEAR
          </button>

          <button
            onClick={handleCloseTable}
            className={`${buttonSizes.xs} bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-lg`}
            data-testid="close-table-button"
          >
            CLOSE
          </button>

          <button
            onClick={handleSaveRound}
            disabled={saving || !winner}
            className={`${buttonSizes.sm} bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg
                       hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500
                       disabled:cursor-not-allowed shadow-lg`}
            data-testid="save-round-button"
          >
            {saving ? 'Saving...' : 'SAVE ROUND'}
          </button>
        </div>
      </div>

      {/* Add Player Dialog */}
      <Dialog open={showAddPlayer} onOpenChange={setShowAddPlayer}>
        <DialogContent className="bg-white border-gray-300 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add Player to Current Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Method 1: Search Members</label>
              <input
                placeholder="Search by Member No / Name / Mobile"
                value={newPlayerSearch}
                onChange={(e) => handleAddPlayerSearch(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="add-player-search-input"
              />
              {searchResults.length > 0 && (
                <div className="mt-2 bg-white border border-gray-300 rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => handleAddMember(m)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-gray-900"
                    >
                      {m.profile_picture ? (
                        <img src={getImageUrl(m.profile_picture)} className="w-10 h-10 rounded-full" alt="" />
                      ) : (
                        <UserCircle className="w-8 h-8 text-gray-400" />
                      )}
                      <span>{m.applicant_name} ({m.membership_no})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Method 2: Add Manual Player</label>
              <div className="flex gap-2">
                <input
                  placeholder="Player Name"
                  value={manualPlayer}
                  onChange={(e) => setManualPlayer(e.target.value)}
                  className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="add-manual-player-input"
                />
                <button
                  onClick={handleAddManualPlayer}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  data-testid="confirm-add-manual-player"
                >
                  Add
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowAddPlayer(false)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Player Dialog */}
      <Dialog open={showRemovePlayer} onOpenChange={setShowRemovePlayer}>
        <DialogContent className="bg-white border-gray-300 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Remove Player from Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-yellow-600">⚠️ This will recalculate Kitty Points</p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {activePlayers.map(player => {
                const pid = String(player.player_id);
                return (
                  <button
                    key={pid}
                    onClick={() => handleRemovePlayer(pid)}
                    className="w-full px-4 py-3 bg-gray-50 hover:bg-red-50 text-gray-900 rounded-lg flex items-center gap-3 transition-colors border border-gray-200 hover:border-red-300"
                    data-testid={`remove-player-${pid}`}
                  >
                    {player.profile_picture ? (
                      <img src={getImageUrl(player.profile_picture)} className="w-10 h-10 rounded-full" alt="" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold">
                        {player.player_name.charAt(0)}
                      </div>
                    )}
                    <span>{player.player_name}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setShowRemovePlayer(false)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Closed Tables with Integrated Reports
const ClosedTables = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) fetchTables();
  }, [dateFrom, dateTo, typeFilter]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const params = { status: 'CLOSED' };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (typeFilter !== 'ALL') params.table_type = typeFilter;
      const response = await api.get('/api/kitty/tables', { params });
      setTables(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this game? This action cannot be undone.')) return;
    try {
      await api.delete(`/api/kitty/tables/${id}`);
      showSuccess('Game deleted successfully');
      fetchTables();
    } catch (err) {
      showError('Failed to delete game');
    }
  };

  const handleExportGamePDF = async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (typeFilter !== 'ALL') params.table_type = typeFilter;
      
      const response = await api.get('/api/kitty/tables', { params: { ...params, status: 'CLOSED' } });
      const games = response.data;
      
      const pdf = await generateGameReportPDF(games, params);
      const filterName = typeFilter === 'ALL' ? 'All' : typeFilter;
      downloadPDF(pdf, `Game_Report_${filterName}_${dateFrom}_${dateTo}.pdf`);
      showSuccess('Game report exported successfully');
    } catch (err) {
      showError('Failed to export game report');
    }
  };

  const handleExportRoundPDF = async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (typeFilter !== 'ALL') params.table_type = typeFilter;
      
      const response = await api.get('/api/kitty/tables', { params: { ...params, status: 'CLOSED' } });
      const games = response.data;
      
      const pdf = await generateRoundReportPDF(games);
      const filterName = typeFilter === 'ALL' ? 'All' : typeFilter;
      downloadPDF(pdf, `Round_Report_${filterName}_${dateFrom}_${dateTo}.pdf`);
      showSuccess('Round report exported successfully');
    } catch (err) {
      showError('Failed to export round report');
    }
  };

  const handlePrintGameReport = () => {
    window.print();
  };

  const groupedByDate = useMemo(() => {
    return tables.reduce((acc, table) => {
      const date = table.started_at?.slice(0, 10);
      if (!acc[date]) acc[date] = [];
      acc[date].push(table);
      return acc;
    }, {});
  }, [tables]);

  const totalKP = useMemo(() => {
    return tables.reduce((sum, t) => sum + (t.total_kitty || 0), 0);
  }, [tables]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="closed-tables">
      <style>{styles}</style>

      {/* Print Header */}
      <div className="print-header">
        <h1 className="text-3xl font-bold text-gray-900">KITTY RUMMY - CLOSED TABLES REPORT</h1>
        <p className="text-lg text-gray-700 mt-2">Report Period: {dateFrom} to {dateTo}</p>
        <p className="text-base text-gray-600">Type Filter: {typeFilter}</p>
        <p className="text-base text-gray-600">Generated: {new Date().toLocaleString()}</p>
        <p className="text-xl font-bold text-green-600 mt-2">Total KP: {totalKP}</p>
      </div>

      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Closed Tables & Reports</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Showing {tables.length} Games • Total KP: <span className="font-bold text-green-600">{totalKP}</span>
          </p>
        </div>
      </div>

      <div className="no-print bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <div className="flex flex-col gap-4">
          {/* Date and Type Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                data-testid="date-from-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                data-testid="date-to-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type Filter</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                data-testid="type-filter-select"
              >
                <option value="ALL">All Types</option>
                <option value="STAKE">STAKE Only</option>
                <option value="SYNDICATE">SYNDICATE Only</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchTables}
                className={`${buttonSizes.md} w-full bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold`}
                data-testid="apply-filter-button"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExportGamePDF}
              className={`${buttonSizes.sm} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2`}
              data-testid="export-game-pdf-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export Game Report (PDF)</span>
              <span className="sm:hidden">Game PDF</span>
            </button>

            <button
              onClick={handleExportRoundPDF}
              className={`${buttonSizes.sm} bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center gap-2`}
              data-testid="export-round-pdf-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export Round Report (PDF)</span>
              <span className="sm:hidden">Round PDF</span>
            </button>

            <button
              onClick={handlePrintGameReport}
              className={`${buttonSizes.sm} bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-semibold flex items-center gap-2`}
              data-testid="print-report-button"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Print Report</span>
              <span className="sm:hidden">Print</span>
            </button>
          </div>
        </div>
      </div>

      {Object.keys(groupedByDate).length === 0 ? (
        <div className="bg-white rounded-xl p-8 sm:p-12 text-center border border-gray-200">
          <p className="text-gray-600">No closed tables found for the selected filters</p>
        </div>
      ) : (
        Object.entries(groupedByDate).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dayTables]) => {
          const dateObj = new Date(date);
          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
          const totalGames = dayTables.length;
          const totalRounds = dayTables.reduce((sum, t) => sum + (t.rounds_count || 0), 0);
          const totalKP = dayTables.reduce((sum, t) => sum + (t.total_kitty || 0), 0);

          return (
            <div key={date} className="space-y-3 sm:space-y-4">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 border border-gray-700">
                <h2 className="text-base sm:text-lg font-bold">
                  {dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ({dayName})
                </h2>
                <div className="text-xs sm:text-sm flex flex-wrap gap-2">
                  <span className="font-semibold">Games: {totalGames}</span>
                  <span className="hidden sm:inline">|</span>
                  <span className="font-semibold">Rounds: {totalRounds}</span>
                  <span className="hidden sm:inline">|</span>
                  <span className="font-semibold">KP: {totalKP}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {dayTables.map(table => {
                  const allScores = table.rounds?.flatMap(r => r.scores || []) || [];
                  const playerTotals = allScores.reduce((acc, score) => {
                    if (!acc[score.player_name]) {
                      acc[score.player_name] = { total: 0, name: score.player_name };
                    }
                    acc[score.player_name].total += score.points || 0;
                    return acc;
                  }, {});

                  const sortedPlayers = Object.values(playerTotals).sort((a, b) => b.total - a.total);
                  const winner = sortedPlayers[0];
                  const loser = sortedPlayers[sortedPlayers.length - 1];

                  return (
                    <div key={table.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200 hover:border-gray-300 transition-all" data-testid={`closed-table-${table.id}`}>
                      <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                            <h3 className="text-base sm:text-xl font-bold text-gray-900">
                              Game {table.game_no} • Table {table.table_no} • Group {table.group_name}
                            </h3>
                            <span className={`px-2 sm:px-3 py-1 rounded text-xs font-semibold ${
                              table.table_type === 'STAKE' ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-purple-100 text-purple-700 border border-purple-300'
                            }`}>
                              {table.table_type}
                            </span>
                          </div>

                          <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                            <div className="flex flex-wrap gap-2">
                              <span><span className="font-medium">Dealer:</span> {table.dealer || '—'}</span>
                              <span className="hidden sm:inline">|</span>
                              <span><span className="font-medium">Sup:</span> {table.supervisor || '—'}</span>
                              <span className="hidden sm:inline">|</span>
                              <span><span className="font-medium">Cashier:</span> {table.cashier || '—'}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span><span className="font-medium">Start:</span> {new Date(table.started_at).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}</span>
                              <span className="hidden sm:inline">|</span>
                              <span><span className="font-medium">End:</span> {table.closed_at ? new Date(table.closed_at).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'}) : '—'}</span>
                              <span className="hidden sm:inline">|</span>
                              <span><span className="font-medium">Duration:</span> {table.duration || '—'}</span>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg text-xs sm:text-sm">
                              <span className="font-bold">Total Kitty: {table.total_kitty || 0} KP</span>
                               
                            </div>
                        {table.table_type === 'SYNDICATE' && (
                          <div className="mt-2 no-print flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              placeholder="Manual KP"
                              className="border border-gray-300 rounded-lg px-3 py-1 text-xs w-28"
                              value={table._manualKittyInput || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setTables(prev =>
                                  prev.map(t =>
                                    t.id === table.id
                                      ? { ...t, _manualKittyInput: value }
                                      : t
                                  )
                                );
                              }}

                            />

                            <button
                              onClick={async () => {
                                  const kp = Number(table._manualKittyInput);

                                  // ✅ validation
                                  if (!Number.isInteger(kp) || kp <= 0) {
                                    showError('Enter a valid kitty point value');
                                    return;
                                  }

                                  if (!window.confirm('Add manual kitty for this SYNDICATE table?')) return;

                                  try {
                                    await api.post(
                                      `/api/kitty/tables/${table.id}/manual-kitty`,
                                      { kitty_points: kp }
                                    );

                                    showSuccess('Manual kitty added');

                                    // ✅ clear input after success
                                    setTables(prev =>
                                      prev.map(t =>
                                        t.id === table.id
                                          ? { ...t, _manualKittyInput: '' }
                                          : t
                                      )
                                    );

                                    fetchTables();
                                  } catch (err) {
                                    showError('Failed to add manual kitty');
                                  }
                                }}

                              className="px-3 py-1 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700"
                            >
                              ADD KP
                            </button>
                          </div>
                        )}

                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 text-gray-900 rounded-lg text-xs sm:text-sm border border-gray-300">
                              <span className="font-bold">👥 Members: {table.players?.length || 0}</span>
                            </div>
                            <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 text-gray-900 rounded-lg text-xs sm:text-sm border border-gray-300">
                              <span className="font-bold">Rounds: {table.rounds_count || 0}</span>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-3">
                            {winner && (
                              <p className="text-green-600 font-bold flex items-center gap-1 text-xs sm:text-sm">
                                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                                Winner: {winner.name} (+{winner.total})
                              </p>
                            )}
                            {loser && (
                              <p className="text-red-600 font-bold flex items-center gap-1 text-xs sm:text-sm">
                                <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
                                Loser: {loser.name} (-{Math.abs(loser.total)})
                              </p>

                            )}
                          </div>
                        </div>

                        <div className="flex flex-row lg:flex-col gap-2 lg:ml-4">
                          <div className="flex gap-2 lg:flex-col">
  {/* VIEW BUTTON */}
  <button
    onClick={() => navigate(`/kitty/game/${table.id}/report`)}
    className={`
      ${buttonSizes.sm}
      flex-1 lg:flex-none
      bg-gradient-to-r from-blue-600 to-blue-700
      hover:from-blue-700 hover:to-blue-800
      text-white font-semibold
      rounded-lg
      shadow-md hover:shadow-lg
      active:scale-[0.98]
      transition-all duration-200
      flex items-center justify-center gap-2
    `}
    data-testid={`view-game-${table.id}`}
  >
    <Eye className="w-4 h-4 opacity-90" />
    <span className="hidden sm:inline">View Report</span>
    <span className="sm:hidden">View</span>
  </button>

  {/* DELETE BUTTON */}
  <button
    onClick={() => handleDelete(table.id)}
    className={`
      ${buttonSizes.sm}
      flex-1 lg:flex-none
      bg-gradient-to-r from-red-600 to-red-700
      hover:from-red-700 hover:to-red-800
      text-white font-semibold
      rounded-lg
      shadow-md hover:shadow-lg
      active:scale-[0.98]
      transition-all duration-200
      flex items-center justify-center gap-2
    `}
    data-testid={`delete-game-${table.id}`}
  >
    <Trash2 className="w-4 h-4 opacity-90" />
    <span className="hidden sm:inline">Delete Game</span>
    <span className="sm:hidden">Delete</span>
  </button>
</div>

                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// Players List
const PlayersList = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('games');

  useEffect(() => { fetchPlayers(); }, []);

  const fetchPlayers = async () => {
    try {
      const response = await api.get('/api/kitty/players');
      setPlayers(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers = useMemo(() => {
    return players
      .filter(p =>
        !searchQuery ||
        p.player_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.member_no?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === 'games') {
          return (b.games_played || 0) - (a.games_played || 0);
        } else {
          return (b.net_winloss || 0) - (a.net_winloss || 0);
        }
      });
  }, [players, searchQuery, sortBy]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="players-list">
      <style>{styles}</style>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Players</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">View all players (members & guests) who participated in Kitty games</p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 border border-gray-200 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by Name / Member No"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          data-testid="search-players-input"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          data-testid="sort-by-select"
        >
          <option value="games">Sort by: Games Played</option>
          <option value="winloss">Sort by: Net Win/Loss</option>
        </select>
      </div>

      {filteredPlayers.length === 0 ? (
        <div className="bg-white rounded-xl p-8 sm:p-12 text-center border border-gray-200">
          <p className="text-gray-600">No players found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">SL</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Photo</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Member No</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Name</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Games</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Net Win/Loss</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map((player, index) => {
                const netWinLoss = player.net_winloss || 0;
                const isPositive = netWinLoss >= 0;

                return (
                  <tr key={player.player_id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-900 text-sm">{index + 1}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      {player.profile_picture ? (
                        <img src={getImageUrl(player.profile_picture)} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover" alt={player.player_name} />
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-base sm:text-lg">
                          {player.player_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-900 font-medium text-sm">
                      {player.member_no || <span className="text-gray-500 italic">Guest</span>}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-900 font-semibold text-sm">{player.player_name}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-900 text-sm">{player.games_played || 0}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className={`font-bold flex items-center gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {isPositive ? '+' : ''}{netWinLoss.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <button
                        onClick={() => navigate(`/kitty/players/${player.player_id}`)}
                        className={`${buttonSizes.sm} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold`}
                        data-testid={`view-player-${player.player_id}`}
                      >
                        VIEW
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Staff Management
const StaffManagement = () => {
  const { showSuccess, showError } = useToast();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [formData, setFormData] = useState({ name: '', mobile: '', staff_type: 'Dealer' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('ALL');

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/api/kitty/staff');
      setStaff(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editStaff) {
        await api.put(`/api/kitty/staff/${editStaff.id}`, formData);
        showSuccess('Staff updated successfully');
      } else {
        await api.post('/api/kitty/staff', formData);
        showSuccess('Staff added successfully');
      }
      setShowForm(false);
      setEditStaff(null);
      setFormData({ name: '', mobile: '', staff_type: 'Dealer' });
      fetchStaff();
    } catch (err) {
      showError('Failed to save staff');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await api.delete(`/api/kitty/staff/${id}`);
      showSuccess('Staff deleted successfully');
      fetchStaff();
    } catch (err) {
      showError('Failed to delete staff');
    }
  };

  const filteredStaff = useMemo(() => {
    return staff.filter(s => {
      const matchRole = filterRole === 'ALL' || s.staff_type === filterRole;
      const matchSearch = !searchQuery ||
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.mobile?.includes(searchQuery);
      return matchRole && matchSearch;
    });
  }, [staff, filterRole, searchQuery]);

  if (loading) return <LoadingSpinner />;

  const grouped = {
    Supervisor: filteredStaff.filter(s => s.staff_type === 'Supervisor'),
    Dealer: filteredStaff.filter(s => s.staff_type === 'Dealer'),
    Cashier: filteredStaff.filter(s => s.staff_type === 'Cashier')
  };

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="staff-management">
      <style>{styles}</style>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">{filteredStaff.length} staff member(s)</p>
        </div>
        <button
          onClick={() => {
            setEditStaff(null);
            setFormData({ name: '', mobile: '', staff_type: 'Dealer' });
            setShowForm(true);
          }}
          className={`${buttonSizes.md} bg-gradient-to-r from-green-600 to-green-700 text-white font-bold rounded-lg hover:from-green-700 hover:to-green-800 shadow-lg w-full sm:w-auto`}
          data-testid="add-staff-button"
        >
          + Add Staff
        </button>
      </div>

      <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <input
            type="text"
            placeholder="Search by name or mobile..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            data-testid="search-staff-input"
          />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            data-testid="filter-role-select"
          >
            <option value="ALL">All Roles</option>
            <option value="Supervisor">Supervisors</option>
            <option value="Dealer">Dealers</option>
            <option value="Cashier">Cashiers</option>
          </select>
        </div>
      </div>

      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="py-3 px-4 sm:px-6 bg-gray-100 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">{type}s ({items.length})</h3>
          </div>
          <div className="p-4 sm:p-6">
            {items.length === 0 ? (
              <p className="text-center text-gray-500 py-4 text-sm">No {type.toLowerCase()}s added</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">SL</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Type</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Mobile</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((s, index) => (
                      <tr key={s.id} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="px-3 sm:px-4 py-3 text-gray-900 text-sm">{index + 1}</td>
                        <td className="px-3 sm:px-4 py-3 text-gray-900 text-sm">{s.staff_type}</td>
                        <td className="px-3 sm:px-4 py-3 text-gray-900 font-semibold text-sm">{s.name}</td>
                        <td className="px-3 sm:px-4 py-3 text-gray-600 text-sm">{s.mobile || '—'}</td>
                        <td className="px-3 sm:px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => {
                                setEditStaff(s);
                                setFormData({ name: s.name, mobile: s.mobile, staff_type: s.staff_type });
                                setShowForm(true);
                              }}
                              className={`${buttonSizes.xs} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold`}
                              data-testid={`edit-staff-${s.id}`}
                            >
                              EDIT
                            </button>
                            <button
                              onClick={() => handleDelete(s.id)}
                              className={`${buttonSizes.xs} bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold`}
                              data-testid={`delete-staff-${s.id}`}
                            >
                              DELETE
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ))}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white border-gray-300 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{editStaff ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Staff Type *</label>
              <select
                value={formData.staff_type}
                onChange={(e) => setFormData(prev => ({...prev, staff_type: e.target.value}))}
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="staff-type-select"
              >
                <option value="Supervisor">Supervisor</option>
                <option value="Dealer">Dealer</option>
                <option value="Cashier">Cashier</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                required
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="staff-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label>
              <input
                value={formData.mobile}
                onChange={(e) => setFormData(prev => ({...prev, mobile: e.target.value}))}
                required
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="staff-mobile-input"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                data-testid="submit-staff-button"
              >
                {editStaff ? 'Update' : 'Add Staff'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ✅ GAME REPORT VIEW - Round-wise Detailed Report
const GameReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGameReport();
  }, [id]);

  const fetchGameReport = async () => {
    try {
      const response = await api.get(`/api/kitty/tables/${id}`);
      setTable(response.data);
    } catch (err) {
      console.error(err);
      showError('Failed to load game report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const pdf = await generateRoundReportPDF([table]);
      downloadPDF(pdf, `Game_${table.game_no}_Round_Report.pdf`);
      showSuccess('PDF exported successfully');
    } catch (err) {
      showError('Failed to export PDF');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <LoadingSpinner />;
  if (!table) return <EmptyState title="Game not found" />;

  // Calculate final standings
  const allScores = table.rounds?.flatMap(r => r.scores || []) || [];
  const playerTotals = allScores.reduce((acc, score) => {
    const pid = String(score.player_id);
    if (!acc[pid]) {
      acc[pid] = {
        player_id: pid,
        player_name: score.player_name,
        total: 0,
        rounds_played: 0,
        rounds_won: 0
      };
    }
    acc[pid].total += score.points || 0;
    acc[pid].rounds_played += 1;
    if (score.is_winner) acc[pid].rounds_won += 1;
    return acc;
  }, {});

  const finalStandings = Object.values(playerTotals).sort((a, b) => b.total - a.total);
  const topWinner = finalStandings[0];
  const topLoser = finalStandings[finalStandings.length - 1];

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="game-report-view">
      <style>{styles}</style>

      {/* Print Header */}
      <div className="print-header">
        <h1 className="text-3xl font-bold text-gray-900">KITTY RUMMY - GAME DETAILED REPORT</h1>
        <p className="text-xl text-gray-700 mt-2">{table.game_no} ({table.table_type})</p>
        <p className="text-base text-gray-600">Generated: {new Date().toLocaleString('en-GB')}</p>
      </div>

      {/* Header */}
      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {table.game_no} DETAILED REPORT ({table.table_type})
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Round-wise Game Analysis</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExportPDF}
            className={`${buttonSizes.sm} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2`}
            data-testid="export-pdf-button"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export PDF</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button
            onClick={handlePrint}
            className={`${buttonSizes.sm} bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-semibold flex items-center gap-2`}
            data-testid="print-button"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Print</span>
          </button>
          <button
            onClick={() => navigate('/kitty/closed')}
            className={`${buttonSizes.sm} bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold`}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Game Summary Card */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-700 text-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Game Summary</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Game No</p>
            <p className="text-lg font-bold">{table.game_no}</p>
          </div>
          <div>
            <p className="text-gray-400">Table / Group</p>
            <p className="text-lg font-bold">Table {table.table_no} • Group {table.group_name}</p>
          </div>
          <div>
            <p className="text-gray-400">Type</p>
            <p className="text-lg font-bold">{table.table_type}</p>
          </div>
          <div>
            <p className="text-gray-400">Members</p>
            <p className="text-lg font-bold">{table.players?.length || 0}</p>
          </div>
          <div>
            <p className="text-gray-400">Total Rounds</p>
            <p className="text-lg font-bold">{table.rounds_count || 0}</p>
          </div>
          <div>
            <p className="text-gray-400">Total Kitty</p>
            <p className="text-lg font-bold text-yellow-400">{table.total_kitty || 0} KP</p>
          </div>
          <div>
            <p className="text-gray-400">Start Time</p>
            <p className="text-base font-semibold">
              {table.started_at ? new Date(table.started_at).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }) : '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-400">Close Time</p>
            <p className="text-base font-semibold">
              {table.closed_at ? new Date(table.closed_at).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }) : '—'}
            </p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Dealer</p>
              <p className="font-semibold">{table.dealer || '—'}</p>
            </div>
            <div>
              <p className="text-gray-400">Supervisor</p>
              <p className="font-semibold">{table.supervisor || '—'}</p>
            </div>
            <div>
              <p className="text-gray-400">Cashier</p>
              <p className="font-semibold">{table.cashier || '—'}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-700">
          <p className="text-gray-400 mb-2">Duration</p>
          <p className="text-xl font-bold">{table.duration || '—'}</p>
        </div>
      </div>

      {/* Top Winner & Loser */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 sm:p-6 shadow-lg border-2 border-green-300">
          <div className="flex items-center gap-3 mb-3">
            <Trophy className="w-8 h-8 text-green-600" />
            <h3 className="text-lg font-bold text-gray-900">Top Winner</h3>
          </div>
          {topWinner ? (
            <div>
              <p className="text-2xl font-bold text-green-700">{topWinner.player_name}</p>
              <p className="text-xl font-semibold text-green-600 mt-2">+{topWinner.total} Points</p>
              <p className="text-sm text-gray-600 mt-1">
                Rounds Won: {topWinner.rounds_won} / {topWinner.rounds_played}
              </p>
            </div>
          ) : (
            <p className="text-gray-500">—</p>
          )}
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 sm:p-6 shadow-lg border-2 border-red-300">
          <div className="flex items-center gap-3 mb-3">
            <TrendingDown className="w-8 h-8 text-red-600" />
            <h3 className="text-lg font-bold text-gray-900">Top Loser</h3>
          </div>
          {topLoser && topLoser !== topWinner ? (
            <div>
              <p className="text-2xl font-bold text-red-700">{topLoser.player_name}</p>
              <p className="text-xl font-semibold text-red-600 mt-2">
  -{Math.abs(topLoser.total)} Points
</p>

              <p className="text-sm text-gray-600 mt-1">
                Rounds Won: {topLoser.rounds_won} / {topLoser.rounds_played}
              </p>
            </div>
          ) : (
            <p className="text-gray-500">—</p>
          )}
        </div>
      </div>

      {/* Round-wise Details */}
      <div className="space-y-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Round-wise Details</h2>

        {table.rounds && table.rounds.length > 0 ? (
          table.rounds.map((round, roundIndex) => (
            <div key={roundIndex} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Round Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-white">
                    ROUND {round.round_no}
                  </h3>
                  <div className="flex flex-wrap gap-2 text-white text-sm">
                    <span className="px-3 py-1 bg-white/20 rounded-lg font-semibold">
                      {round.active_players || 0} Players
                    </span>
                    {table.table_type === 'STAKE' && (
                      <span className="px-3 py-1 bg-yellow-500/30 rounded-lg font-semibold">
                        {round.kitty_points || 0} KP
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Round Scores Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">SL</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Member Name</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Points (Net)</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {round.scores && round.scores.length > 0 ? (
                      round.scores.map((score, scoreIndex) => {
                        const isWinner = score.is_winner;
                        const isDropped = score.action === 'DROP';

                        return (
                          <tr
                            key={scoreIndex}
                            className={`border-t border-gray-200 ${
                              isWinner ? 'bg-green-50' : isDropped ? 'bg-red-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">{scoreIndex + 1}</td>
                            <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-gray-900">
                              {score.player_name}
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-sm">
                              <span className={`font-bold ${
                                score.points >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {score.points >= 0 ? '+' : ''}{score.points}
                              </span>
                            </td>
                            <td className="px-3 sm:px-4 py-3">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                isWinner
                                  ? 'bg-green-100 text-green-700'
                                  : isDropped
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {isWinner ? '🏆 WINNER' : isDropped ? '❌ DROPPED' : 'LOSER'}
                              </span>
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-gray-600">
                              {score.action === 'SHOW'
                                ? 'SHOW'
                                : score.action === 'DROP'
                                ? `DROP (${score.points})`
                                : 'SCORE ENTRY'}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          No scores recorded for this round
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <p className="text-gray-500">No rounds played yet</p>
          </div>
        )}
      </div>

      {/* Final Standings */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 sm:px-6 py-3 sm:py-4">
          <h3 className="text-lg sm:text-xl font-bold text-white">Final Standings</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Rank</th>
                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Player Name</th>
                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Total Points</th>
                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Rounds Played</th>
                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Rounds Won</th>
                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Win %</th>
              </tr>
            </thead>
            <tbody>
              {finalStandings.map((player, index) => {
                const winPercentage = player.rounds_played > 0
                  ? ((player.rounds_won / player.rounds_played) * 100).toFixed(1)
                  : 0;

                return (
                  <tr
                    key={player.player_id}
                    className={`border-t border-gray-200 ${
                      index === 0 ? 'bg-green-50' : index === finalStandings.length - 1 ? 'bg-red-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-3 sm:px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        index === 0
                          ? 'bg-yellow-400 text-yellow-900'
                          : index === 1
                          ? 'bg-gray-300 text-gray-900'
                          : index === 2
                          ? 'bg-orange-300 text-orange-900'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-gray-900">
                      {player.player_name}
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <span className={`text-base font-bold ${
                        player.total >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {player.total >= 0 ? '+' : ''}{player.total}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">{player.rounds_played}</td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">{player.rounds_won}</td>
                    <td className="px-3 sm:px-4 py-3">
                      <span className={`text-sm font-semibold ${
                        winPercentage >= 50 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {winPercentage}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ✅ PLAYER PROFILE VIEW - Complete Player Statistics & Game History
const PlayerProfileView = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [player, setPlayer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchPlayerProfile();
    }
  }, [playerId, dateFrom, dateTo]);

  const fetchPlayerProfile = async () => {
    try {
      setLoading(true);
      
      // Fetch player basic info
      const playerResponse = await api.get(`/api/kitty/players/${playerId}`);
      setPlayer(playerResponse.data);

      // Fetch player game history
      const historyResponse = await api.get(`/api/kitty/players/${playerId}/history`, {
        params: { date_from: dateFrom, date_to: dateTo }
      });
      setHistory(historyResponse.data);
    } catch (err) {
      console.error(err);
      showError('Failed to load player profile');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const pdf = await generatePlayerHistoryPDF(player, history);
      downloadPDF(pdf, `Player_${player.player_name}_History.pdf`);
      showSuccess('Player history exported successfully');
    } catch (err) {
      showError('Failed to export PDF');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!player) return <EmptyState title="Player not found" />;

  // Calculate statistics
  const gamesWon = history.filter(h => h.is_winner).length;
  const gamesLost = history.length - gamesWon;
  const winPercentage = history.length > 0 ? ((gamesWon / history.length) * 100).toFixed(2) : 0;
  const netWinLoss = player.net_winloss || 0;
  const isPositive = netWinLoss >= 0;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in p-2 sm:p-4" data-testid="player-profile-view">
      <style>{styles}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Player Profile</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Complete statistics and game history</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExportPDF}
            className={`${buttonSizes.sm} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2`}
            data-testid="export-player-history-pdf"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export History</span>
            <span className="sm:hidden">Export</span>
          </button>
          <button
            onClick={() => navigate('/kitty/players')}
            className={`${buttonSizes.sm} bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold`}
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Player Info Card */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 sm:p-8 shadow-lg text-white">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Profile Picture */}
          <div className="flex-shrink-0">
            {player.profile_picture ? (
              <img
                src={getImageUrl(player.profile_picture)}
                alt={player.player_name}
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white text-blue-600 flex items-center justify-center text-4xl sm:text-5xl font-bold border-4 border-white shadow-lg">
                {player.player_name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Player Details */}
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">{player.player_name}</h2>
            <div className="space-y-1">
              {player.member_no ? (
                <p className="text-lg text-blue-100">
                  Member No: <span className="font-semibold">{player.member_no}</span>
                </p>
              ) : (
                <p className="text-lg text-blue-100 italic">Guest Player</p>
              )}
              {player.mobile && (
                <p className="text-base text-blue-100">Mobile: {player.mobile}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 sm:px-6 py-3 sm:py-4">
          <h3 className="text-lg sm:text-xl font-bold text-white">Overall Statistics</h3>
        </div>

        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Games</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">{player.games_played || 0}</p>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Games Won</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-600">{gamesWon}</p>
            </div>

            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Games Lost</p>
              <p className="text-2xl sm:text-3xl font-bold text-red-600">{gamesLost}</p>
            </div>

            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Win Percentage</p>
              <p className="text-2xl sm:text-3xl font-bold text-blue-600">{winPercentage}%</p>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-lg col-span-2">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Net Win/Loss</p>
              <p className={`text-2xl sm:text-3xl font-bold flex items-center justify-center gap-2 ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
                {isPositive ? '+' : ''}{netWinLoss.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Filter Game History</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              data-testid="date-from-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              data-testid="date-to-input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchPlayerProfile}
              className={`${buttonSizes.md} w-full bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold`}
              data-testid="apply-date-filter-button"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>

      {/* Game History */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 sm:px-6 py-3 sm:py-4">
          <h3 className="text-lg sm:text-xl font-bold text-white">
            Game History ({history.length} games)
          </h3>
        </div>

        {history.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No game history found for the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">SL</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Game No</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Type</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Table</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Rounds</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Result</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Points</th>
                  <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map((game, index) => {
                  const isWinner = game.is_winner;
                  const gamePoints = game.total_points || 0;

                  return (
                    <tr
                      key={index}
                      className={`border-t border-gray-200 ${
                        isWinner ? 'bg-green-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">
                        {new Date(game.game_date).toLocaleDateString('en-GB')}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-sm font-semibold text-gray-900">
                        {game.game_no}
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                          game.table_type === 'STAKE'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {game.table_type}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">T-{game.table_no}</td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-900">{game.rounds_played || 0}</td>
                      <td className="px-3 sm:px-4 py-3">
                        {isWinner ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                            <Trophy className="w-3 h-3" />
                            Winner
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold">
                            Player
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <span className={`text-sm font-bold ${
                          gamePoints >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {gamePoints >= 0 ? '+' : ''}{gamePoints}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <button
                          onClick={() => navigate(`/kitty/game/${game.table_id}/report`)}
                          className={`${buttonSizes.xs} bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold`}
                          data-testid={`view-game-${game.table_id}`}
                        >
                          VIEW
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Kitty Component
const Kitty = () => {
  return (
    <LayoutWrapper>
      <Routes>
  <Route index element={<KittyDashboard />} />
  <Route path="create" element={<CreateTable />} />
  <Route path="live" element={<LiveTables />} />
  <Route path="game/:id" element={<LiveGame />} />

  {/* ✅ ADD THESE TWO */}
  <Route path="game/:id/report" element={<GameReportView />} />
  <Route path="players/:playerId" element={<PlayerProfileView />} />

  <Route path="closed" element={<ClosedTables />} />
  <Route path="players" element={<PlayersList />} />
  <Route path="staff" element={<StaffManagement />} />
</Routes>

    </LayoutWrapper>
  );
};

export default Kitty;
